import React, { useState, useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { CalendarIcon, ClockIcon, LoaderIcon, CopyIcon, CheckIcon } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Alert, AlertDescription } from './ui/alert'
import { useAppStore } from '../stores/useAppStore'
import { workOrderService, type SchedulingRequest, type SchedulingResult } from '../services/workOrderService'

interface DayPlanningModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const DayPlanningModal: React.FC<DayPlanningModalProps> = ({
  open,
  onOpenChange
}) => {
  const { addNotification } = useAppStore()
  const [copied, setCopied] = useState(false)
  const logRef = useRef<HTMLPreElement>(null)

  // Calculate default dates: start = 5 days from now, end = 9 days from now
  const getDefaultStartDate = () => {
    const date = new Date()
    date.setDate(date.getDate() + 5)
    return date.toISOString().split('T')[0]
  }

  const getDefaultEndDate = () => {
    const date = new Date()
    date.setDate(date.getDate() + 9)
    return date.toISOString().split('T')[0]
  }

  const [startDate, setStartDate] = useState(getDefaultStartDate())
  const [endDate, setEndDate] = useState(getDefaultEndDate())

  const executeMutation = useMutation<SchedulingResult, Error, SchedulingRequest>({
    mutationFn: async (request) => {
      return await workOrderService.executeScheduling(request)
    },
    onSuccess: (data) => {
      let message = data.message

      if (data.success) {
        message = `Scheduling completed! Scheduled ${data.totalJobsScheduled} out of ${data.totalJobsFound} available jobs.`
      } else {
        message = data.message || 'Scheduling failed for an unknown reason.'
      }

      addNotification({
        type: data.success ? 'success' : 'error',
        message,
        timestamp: Date.now()
      })
    },
    onError: (error) => {
      console.error('Scheduling error:', error)
      addNotification({
        type: 'error',
        message: `Failed to execute scheduling: ${error.message}`,
        timestamp: Date.now()
      })
    }
  })

  const handleExecute = () => {
    if (!startDate || !endDate) {
      addNotification({
        type: 'error',
        message: 'Please provide both start date and end date.',
        timestamp: Date.now()
      })
      return
    }

    if (new Date(endDate) <= new Date(startDate)) {
      addNotification({
        type: 'error',
        message: 'End date must be after start date.',
        timestamp: Date.now()
      })
      return
    }

    executeMutation.mutate({
      startDate,
      endDate,
      dryRun: false
    })
  }

  const handleClose = () => {
    if (!executeMutation.isPending) {
      executeMutation.reset()
      onOpenChange(false)
    }
  }

  const handleCopyLog = async () => {
    const logText = executeMutation.data?.logText
    if (logText) {
      await navigator.clipboard.writeText(logText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const hasLog = executeMutation.isSuccess && executeMutation.data?.logText

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className={`bg-white ${hasLog ? 'sm:max-w-4xl max-h-[90vh]' : 'sm:max-w-md'}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Work Order Scheduling Engine
          </DialogTitle>
          <DialogDescription>
            The scheduling engine will automatically schedule unscheduled jobs across the specified date range. This will assign order numbers and scheduled times to work orders based on technician availability and optimal routing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!hasLog && (
            <>
              <div className="space-y-2">
                <Label htmlFor="startDate">Start Date</Label>
                <div className="relative">
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    disabled={executeMutation.isPending}
                    className="pl-10"
                  />
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500">Default: 5 days from today</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="endDate">End Date</Label>
                <div className="relative">
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    disabled={executeMutation.isPending}
                    className="pl-10"
                  />
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>
                <p className="text-xs text-gray-500">Default: 9 days from today</p>
              </div>
            </>
          )}

          {executeMutation.error && (
            <Alert variant="destructive">
              <AlertDescription>
                {executeMutation.error.message}
              </AlertDescription>
            </Alert>
          )}

          {executeMutation.isPending && (
            <Alert>
              <LoaderIcon className="h-4 w-4 animate-spin" />
              <AlertDescription>
                Running scheduling engine... This may take a moment to optimize routes and assign jobs.
              </AlertDescription>
            </Alert>
          )}

          {hasLog && (
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <Label>Scheduling Log</Label>
                <Button variant="outline" size="sm" onClick={handleCopyLog}>
                  {copied ? (
                    <><CheckIcon className="h-3 w-3 mr-1" /> Copied</>
                  ) : (
                    <><CopyIcon className="h-3 w-3 mr-1" /> Copy Log</>
                  )}
                </Button>
              </div>
              <pre
                ref={logRef}
                className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs font-mono overflow-auto max-h-[60vh] whitespace-pre"
              >
                {executeMutation.data.logText}
              </pre>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={executeMutation.isPending}
          >
            {hasLog ? 'Close' : 'Cancel'}
          </Button>
          {!hasLog && (
            <Button
              type="button"
              onClick={handleExecute}
              disabled={executeMutation.isPending || !startDate || !endDate}
            >
              {executeMutation.isPending ? (
                <>
                  <LoaderIcon className="h-4 w-4 animate-spin mr-2" />
                  Scheduling...
                </>
              ) : (
                <>
                  <ClockIcon className="h-4 w-4 mr-2" />
                  Run Scheduling Engine
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
